/**
 * Presence chips: who ELSE has this chat open, and what they are doing.
 * Rendered in the chat header (Inbox) or in the strip above the composer
 * (CRM widget, where the header is hidden).
 *
 * Industry pattern (Zendesk / Freshdesk / Help Scout): icon + name + verb,
 * never a bare avatar — the words are what make it obvious.
 *
 *   [ 👁 Sabariraja viewing ]      grey chip
 *   [ ✏ Sabariraja replying ]      amber chip, pulsing
 *   [ 👁 3 viewing ]               three or more → count
 *
 * Hover → every agent's name + what they are doing.
 */

import { Eye, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';
import type { PresenceUser } from '@/lib/firebase/chat-presence';

const AVATAR = 14;

function initials(nameOrEmail: string): string {
  const s = nameOrEmail.trim();
  if (!s) return '?';
  if (s.includes('@') && !s.includes(' ')) return s[0].toUpperCase();
  const parts = s.split(/\s+/).filter(Boolean);
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** "Sabariraja" · "Iyappan, Vijay" · "3" */
function whoLabel(users: PresenceUser[]): string {
  if (users.length === 1) return users[0].name;
  if (users.length === 2) return `${users[0].name}, ${users[1].name}`;
  return String(users.length);
}

function TinyAvatar({ user }: { user: PresenceUser }) {
  return user.photoURL ? (
    <img src={user.photoURL} alt="" className="rounded-full object-cover shrink-0" style={{ width: AVATAR, height: AVATAR }} />
  ) : (
    <span
      className="inline-flex items-center justify-center rounded-full bg-black/10 dark:bg-white/15 font-bold shrink-0"
      style={{ width: AVATAR, height: AVATAR, fontSize: '8px' }}
    >
      {initials(user.name)}
    </span>
  );
}

interface ChipProps {
  users: PresenceUser[];
  verb: 'viewing' | 'replying';
}

function PresenceChip({ users, verb }: ChipProps) {
  const replying = verb === 'replying';
  const Icon = replying ? Pencil : Eye;
  return (
    <span
      data-testid={`chat-presence-chip-${verb}`}
      className={cn(
        // leading-4 (not leading-none): with overflow-hidden truncation a tight
        // line box clips descenders ("j", "g") and the icon edge.
        'inline-flex items-center gap-1 rounded-full pl-1.5 pr-2 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap max-w-[220px] overflow-hidden',
        replying
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
          : 'bg-muted text-muted-foreground',
      )}
    >
      <Icon className={cn('h-3 w-3 shrink-0', replying && 'animate-pulse')} />
      <TinyAvatar user={users[0]} />
      <span className="truncate min-w-0">{whoLabel(users)} {verb}</span>
    </span>
  );
}

interface ChatPresenceAvatarsProps {
  users: PresenceUser[];
  className?: string;
}

export function ChatPresenceAvatars({ users, className }: ChatPresenceAvatarsProps) {
  if (users.length === 0) return null;

  const replying = users.filter((u) => u.typing);
  const viewing = users.filter((u) => !u.typing);

  // Replying agents first (they matter most), then a capped list so a very
  // busy chat never produces a screen-tall tooltip.
  const TOOLTIP_MAX = 8;
  const ordered = [...replying, ...viewing];
  const shown = ordered.slice(0, TOOLTIP_MAX);
  const more = ordered.length - shown.length;
  const tooltip = (
    <div className="text-left" style={{ minWidth: 160 }}>
      <div className="font-semibold mb-1">Also in this chat ({users.length})</div>
      {shown.map((u) => (
        <div key={u.uid} className="whitespace-nowrap leading-tight mb-1 last:mb-0">
          <div>{u.name}<span className={u.typing ? 'text-amber-300' : 'opacity-70'}> · {u.typing ? 'replying' : 'viewing'}</span></div>
        </div>
      ))}
      {more > 0 && <div className="opacity-70 mt-1">+{more} more</div>}
    </div>
  );

  return (
    <Tooltip content={tooltip} side="bottom">
      <div
        data-testid="chat-presence"
        className={cn('flex items-center gap-1 shrink-0 min-w-0', className)}
        aria-label={`${users.length} other ${users.length === 1 ? 'agent is' : 'agents are'} viewing this chat${replying.length ? `, ${replying.length} typing` : ''}`}
      >
        {replying.length > 0 && <PresenceChip users={replying} verb="replying" />}
        {viewing.length > 0 && <PresenceChip users={viewing} verb="viewing" />}
      </div>
    </Tooltip>
  );
}
